import type { BaseLeagueFilters, LeagueInfo } from '@library/types/leagues';
import type {
  Status,
  UpdateBulkPlayerTeamRequest
} from '@library/types/projection';
import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { LEAGUES_WITH_RELATED_DURATIONS } from '@bo-constants/leagues';
import type { SerializedError } from '@reduxjs/toolkit';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getBulkLeagueIds } from '@bo-utils/leagues';
import { getCalculatedBoardStartTimes } from '@bo-utils/leagueTimes';
import { useUpdateBulkTeamProjectionsMutation } from '@bo-services/projectionsApi';

export interface TradingTeamBulkActions {
  handleChangeBoardTime: (boardTime: DateTime) => void;
  handleChangeStartTime: (boardTime: DateTime) => void;
  handleChangeDisplayTime: (displayTime: DateTime) => void;
  handleChangeStatus: (status: Status) => void;
  handleDelay: (delay: number) => void;
  handleChangeInGame: (inGame: boolean) => void;
  handleChangeGameModes: (gameModeIds: Array<number>) => void;
}

export const useTradingTeamBulkActions = ({
  bulkLeagueIdGroups,
  durationId,
  gameId,
  leagueInfo,
  initialStartTime,
  allStartTimes,
  gameIdWithDuration,
  playerIds,
  baseLeagueFilters = [],
  awayTeam = ''
}: {
  bulkLeagueIdGroups: Array<Array<string>>;
  durationId: string;
  gameId: string;
  leagueInfo: LeagueInfo;
  initialStartTime: string;
  allStartTimes?: string[];
  gameIdWithDuration?: string;
  playerIds: Array<string>;
  baseLeagueFilters?: Array<BaseLeagueFilters>;
  awayTeam?: string;
}): {
  tradingTeamBulkActions: TradingTeamBulkActions;
  bulkUpdateTeamError: FetchBaseQueryError | SerializedError | undefined;
  isBulkUpdateTeamError: boolean;
  isBulkUpdateTeamSuccess: boolean;
  isBulkUpdateTeamUninitialized: boolean;
  resetBulkUpdateTeam: () => void;
} => {
  const assumedNLS = useMemo(
    () => gameIdWithDuration?.includes('NLS'),
    [gameIdWithDuration]
  );

  const leagueId = useMemo(
    () =>
      leagueInfo.baseLeagueFilters.find((league) =>
        league.durations.find((duration) => {
          return duration.leagueProjectionFilterId === durationId;
        })
      )?.leagueId ?? leagueInfo.id,
    [durationId, leagueInfo]
  );

  const leagueName = useMemo(
    () =>
      leagueInfo.baseLeagueFilters.find((league) =>
        league.durations.find((duration) => {
          return duration.leagueProjectionFilterId === durationId;
        })
      )?.name ?? leagueInfo.name,
    [durationId, leagueInfo]
  );

  const bulkLeagueIds = useMemo(
    () => getBulkLeagueIds({ bulkLeagueIdGroups, leagueId }),
    [bulkLeagueIdGroups, leagueId]
  );

  const [
    patchBulkTeam,
    {
      error: bulkUpdateTeamError,
      isError: isBulkUpdateTeamError,
      isSuccess: isBulkUpdateTeamSuccess,
      isUninitialized: isBulkUpdateTeamUninitialized,
      reset: resetBulkUpdateTeam
    }
  ] = useUpdateBulkTeamProjectionsMutation();

  const bulkUpdateTeam = useCallback(
    async ({
      boardTime,
      startTime,
      displayTime,
      status,
      request,
      inGame,
      gameModeIds
    }: {
      boardTime?: string;
      startTime?: string;
      displayTime?: string;
      status?: Status;
      request?: UpdateBulkPlayerTeamRequest;
      inGame?: boolean;
      gameModeIds?: Array<number>;
    }) => {
      let patchBulkTeamRequest: UpdateBulkPlayerTeamRequest;
      if (request) {
        patchBulkTeamRequest = request;
      } else {
        const players = startTime && !assumedNLS ? [] : playerIds;

        let bulkLeagueIdProjections = [];

        // checking for leagues with durations that need to be related
        if (LEAGUES_WITH_RELATED_DURATIONS.includes(leagueName)) {
          const selectedDuration =
            leagueInfo.baseLeagueFilters[0].durations.find(
              (duration) => duration.leagueProjectionFilterId === durationId
            );

          bulkLeagueIdProjections =
            selectedDuration?.leagueProjectionFilterRelatedIds?.map(
              (aDurationId) => {
                return {
                  board_time: boardTime,
                  league_id: leagueId,
                  start_time: startTime,
                  display_time: displayTime,
                  duration_id: Number(aDurationId),
                  status,
                  in_game: inGame,
                  game_mode_ids: gameModeIds
                };
              }
            ) || [];
        } else {
          bulkLeagueIdProjections = bulkLeagueIds.map((bulkLeagueId) => ({
            board_time: boardTime,
            league_id: bulkLeagueId,
            start_time: startTime,
            display_time: displayTime,
            status,
            in_game: inGame,
            game_mode_ids: gameModeIds
          }));
        }

        patchBulkTeamRequest = {
          bulk_request: {
            player_ids: players,
            projections: bulkLeagueIdProjections,
            ...(!assumedNLS && { game_id: gameId }),
            ...(assumedNLS && {
              away_team: awayTeam,
              game_mode_ids: gameModeIds,
              game_start_time:
                allStartTimes && allStartTimes.length
                  ? allStartTimes.map((startTimeOption) =>
                      formatWithTimezoneChange(
                        DateTime.fromISO(startTimeOption),
                        'LL/dd/yyyy t'
                      )
                    )
                  : [
                      formatWithTimezoneChange(
                        DateTime.fromISO(initialStartTime),
                        'LL/dd/yyyy t'
                      )
                    ]
            })
          }
        };
      }

      await patchBulkTeam(patchBulkTeamRequest).catch((error) => {
        // eslint-disable-next-line no-console
        console.log('Error Bulk Updating Projections', error);
      });
    },
    [
      patchBulkTeam,
      assumedNLS,
      playerIds,
      leagueName,
      gameId,
      awayTeam,
      allStartTimes,
      initialStartTime,
      leagueInfo.baseLeagueFilters,
      durationId,
      leagueId,
      bulkLeagueIds
    ]
  );

  const handleChangeBoardTime = useCallback(
    (boardTime: DateTime) => {
      const formattedBoardTime = formatWithTimezoneChange(
        boardTime,
        'LL/dd/yyyy t'
      );

      bulkUpdateTeam({
        boardTime: formattedBoardTime
      }).catch(() => {});
    },
    [bulkUpdateTeam]
  );

  const handleChangeStartTime = useCallback(
    (startTime: DateTime) => {
      const formattedStartTime = formatWithTimezoneChange(
        startTime,
        'LL/dd/yyyy t'
      );

      bulkUpdateTeam({
        startTime: formattedStartTime
      }).catch(() => {});
    },
    [bulkUpdateTeam]
  );

  const handleChangeDisplayTime = useCallback(
    (displayTime: DateTime) => {
      const formattedDisplayTime = formatWithTimezoneChange(
        displayTime,
        'LL/dd/yyyy t'
      );

      bulkUpdateTeam({
        displayTime: formattedDisplayTime
      }).catch(() => {});
    },
    [bulkUpdateTeam]
  );

  const handleChangeStatus = (status: Status) => {
    bulkUpdateTeam({
      status
    }).catch(() => {});
  };

  const handleChangeInGame = (inGame: boolean) => {
    bulkUpdateTeam({
      inGame
    }).catch(() => {});
  };

  const handleChangeGameModes = (gameModeIds: Array<number>) => {
    bulkUpdateTeam({
      gameModeIds
    }).catch(() => {});
  };

  const handleDelay = (delay: number) => {
    const startTimeWithDelay =
      DateTime.fromISO(initialStartTime)
        .plus({ milliseconds: delay })
        .toISO() ?? '';

    const players = assumedNLS ? playerIds : [];
    const request: UpdateBulkPlayerTeamRequest = {
      bulk_request: {
        player_ids: players,
        ...(!assumedNLS && { game_id: gameId, duration_id: durationId }),
        ...(assumedNLS && {
          away_team: awayTeam,
          duration_id: durationId,
          game_start_time:
            allStartTimes && allStartTimes.length
              ? allStartTimes.map((startTimeOption) =>
                  formatWithTimezoneChange(
                    DateTime.fromISO(startTimeOption),
                    'LL/dd/yyyy t'
                  )
                )
              : [
                  formatWithTimezoneChange(
                    DateTime.fromISO(initialStartTime),
                    'LL/dd/yyyy t'
                  )
                ]
        }),
        projections: baseLeagueFilters.map((subLeague) => {
          const {
            boardTime: calculatedBoardTime,
            startTime: calculatedStartTime
          } = getCalculatedBoardStartTimes({
            durationId: '',
            durations: [],
            gameStartTime: startTimeWithDelay,
            leagueName: subLeague.name
          });

          return {
            league_id: subLeague.leagueId,
            duration_id: Number(
              subLeague.durations[0].leagueProjectionFilterId
            ),
            start_time: formatWithTimezoneChange(
              DateTime.fromISO(calculatedStartTime),
              'LL/dd/yyyy t'
            ),
            board_time: formatWithTimezoneChange(
              DateTime.fromISO(calculatedBoardTime),
              'LL/dd/yyyy t'
            )
          };
        })
      }
    };

    bulkUpdateTeam({
      request
    }).catch(() => {});
  };

  const tradingTeamBulkActions: TradingTeamBulkActions = {
    handleChangeBoardTime,
    handleChangeStartTime,
    handleChangeDisplayTime,
    handleChangeStatus,
    handleDelay,
    handleChangeInGame,
    handleChangeGameModes
  };

  return {
    tradingTeamBulkActions,
    bulkUpdateTeamError,
    isBulkUpdateTeamError,
    isBulkUpdateTeamSuccess,
    isBulkUpdateTeamUninitialized,
    resetBulkUpdateTeam
  };
};
